import { getApp, getApps, initializeApp } from "firebase/app";
import {
  browserLocalPersistence,
  browserSessionPersistence,
  connectAuthEmulator,
  getAuth,
  getRedirectResult as firebaseGetRedirectResult,
  inMemoryPersistence,
  OAuthProvider,
  onAuthStateChanged,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithRedirect,
  signOut as firebaseSignOut,
} from "firebase/auth";
import type { User } from "firebase/auth";
import { env } from "@/shared/config/env";
import { browserStorage, storageEntries } from "@/shared/browser-storage";
import type { AuthUser } from "@/features/auth/model/auth-user";

function debugAuth(message: string, error?: unknown): void {
  if (import.meta.env.MODE === "test") {
    return;
  }

  if (!import.meta.env.DEV) {
    return;
  }

  if (error instanceof Error) {
    const errorCode = (error as { code?: string }).code;
    console.debug(`[Auth] ${message}`, { name: error.name, code: errorCode });
    return;
  }

  console.debug(`[Auth] ${message}`);
}

const firebaseApp =
  getApps().length > 0
    ? getApp()
    : initializeApp({
        apiKey: env.VITE_IDENTITY_PLATFORM_API_KEY,
        authDomain: env.VITE_IDENTITY_PLATFORM_AUTH_DOMAIN,
        projectId: env.VITE_IDENTITY_PLATFORM_PROJECT_ID,
      });

export const auth = getAuth(firebaseApp);

function getAuthEmulatorConnectionUrl(): string {
  if (!env.VITE_FIREBASE_AUTH_EMULATOR_URL) {
    return "";
  }

  if (!env.VITE_FIREBASE_AUTH_EMULATOR_USE_APP_ORIGIN) {
    return env.VITE_FIREBASE_AUTH_EMULATOR_URL;
  }

  if (typeof window === "undefined" || !window.location.origin.startsWith("http")) {
    throw new Error(
      "Configuration error: local Firebase Auth emulator proxy requires a browser HTTP(S) origin."
    );
  }

  return window.location.origin;
}

const authEmulatorConnectionUrl = getAuthEmulatorConnectionUrl();

if (authEmulatorConnectionUrl) {
  try {
    connectAuthEmulator(auth, authEmulatorConnectionUrl);
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    if (!errorMessage.includes("already been initialized")) {
      throw error;
    }
  }
}

const defaultTenantId = env.VITE_IDENTITY_PLATFORM_DEFAULT_TENANT_ID;
if (defaultTenantId) {
  auth.tenantId = defaultTenantId;
}

export async function initializeAuthPersistence(requirePersistentStorage = false): Promise<void> {
  try {
    await setPersistence(auth, browserLocalPersistence);
    debugAuth("Persistence: browserLocalPersistence");
    return;
  } catch (localError) {
    debugAuth("browserLocalPersistence not available, trying sessionStorage", localError);
  }

  try {
    await setPersistence(auth, browserSessionPersistence);
    debugAuth("Persistence: browserSessionPersistence");
    return;
  } catch (sessionError) {
    debugAuth("browserSessionPersistence not available", sessionError);
  }

  if (requirePersistentStorage) {
    throw new Error(
      "SSO_STORAGE_UNAVAILABLE: Browser storage is required for SSO sign-in. Please disable private browsing or allow site storage, then try again."
    );
  }

  try {
    await setPersistence(auth, inMemoryPersistence);
    debugAuth("Persistence: inMemoryPersistence");
  } catch (memoryError) {
    debugAuth("All persistence modes failed", memoryError);
    throw new Error(
      "AUTH_STORAGE_FAILED: Cannot initialise authentication storage. Please disable private browsing or check browser storage settings."
    );
  }
}

export function setTenantId(tenantId: string): void {
  auth.tenantId = tenantId;
}

export function clearTenantId(): void {
  auth.tenantId = null;
}

export function toAuthUser(user: User): AuthUser {
  return {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
  };
}

export async function signInWithEmail(email: string, password: string): Promise<AuthUser> {
  await initializeAuthPersistence();
  const credential = await signInWithEmailAndPassword(auth, email, password);
  return toAuthUser(credential.user);
}

export async function signOut(): Promise<void> {
  await firebaseSignOut(auth);
}

export async function getIdToken(): Promise<string | null> {
  const user = auth.currentUser;

  if (!user) {
    return null;
  }

  return user.getIdToken();
}

export function subscribeToAuthState(callback: (user: AuthUser | null) => void): () => void {
  return onAuthStateChanged(auth, (user) => {
    callback(user ? toAuthUser(user) : null);
  });
}

export function markSsoRedirectPending(): void {
  browserStorage.write(storageEntries.ssoRedirectPending, 1);
}

export function clearSsoRedirectPending(): void {
  browserStorage.remove(storageEntries.ssoRedirectPending);
}

export function hasSsoRedirectPending(): boolean {
  return browserStorage.read(storageEntries.ssoRedirectPending) === 1;
}

export async function signInWithSso(providerId: string): Promise<void> {
  if (!auth.tenantId) {
    throw new Error(
      "TENANT_NOT_CONFIGURED: Sign-in could not be started. Please enter your email address and try again."
    );
  }

  await initializeAuthPersistence(true);

  const provider = new OAuthProvider(providerId);

  markSsoRedirectPending();

  try {
    await signInWithRedirect(auth, provider);
  } catch (error) {
    clearSsoRedirectPending();
    throw error;
  }
}

export { firebaseGetRedirectResult as getRedirectResult };
